import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Copy, BookmarkPlus, Save, X } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { attributeErrors, toApiRows, toFormRows } from "@/lib/attributes";
import { btnOutline } from "@/components/app/ui";

const cell = "h-8 w-full rounded-md border bg-white px-2 text-[13px] outline-none transition focus:ring-2";
const ok = "border-slate-300 focus:border-blue-600 focus:ring-blue-600/15";
const bad = "border-red-500 focus:ring-red-500/15";

/**
 * Attribute rows for a product (name / value). Save the rows as a store template and apply it
 * on other products to avoid retyping. Applying copies the rows — editing a template later never changes
 * products that already used it.
 */
export default function AttributesEditor({ rows, onChange, products = [], showErrors = false }) {
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tplName, setTplName] = useState("");
  const [naming, setNaming] = useState(false);

  const loadTemplates = useCallback(() => api.get("/vendor/attribute-templates").then(({ data }) => setTemplates(data)).catch(() => {}), []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const errors = useMemo(() => attributeErrors(rows), [rows]);
  const names = useMemo(() => [...new Set(products.flatMap((p) => (p.attributes || []).map((a) => a.name)).concat(rows.map((r) => r.name)).filter(Boolean))], [products, rows]);
  const filled = toApiRows(rows).length > 0;

  const setRow = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addRow = () => onChange([...rows, { name: "", value: "" }]);
  const removeRow = (i) => onChange(rows.filter((_, j) => j !== i));

  const apply = (attrs, label) => {
    if (!attrs?.length) return;
    if (filled && !window.confirm(`Replace the attributes you've entered with those from “${label}”?`)) return;
    onChange(toFormRows(attrs));
    toast.success(`Copied ${attrs.length} attribute${attrs.length > 1 ? "s" : ""} from “${label}”. Edit them as needed.`);
  };

  const saveTemplate = async () => {
    const bads = Object.keys(errors).length;
    if (bads) return toast.error("Fix the highlighted attribute rows first.");
    if (!tplName.trim()) return toast.error("Give the template a name.");
    setSaving(true);
    try {
      await api.post("/vendor/attribute-templates", { name: tplName.trim(), attributes: toApiRows(rows) });
      toast.success(`Template “${tplName.trim()}” saved.`);
      setTplName(""); setNaming(false); loadTemplates();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const delTemplate = async (t) => {
    if (!window.confirm(`Delete template “${t.name}”? Products that already used it are not affected.`)) return;
    try { await api.delete(`/vendor/attribute-templates/${t.id}`); loadTemplates(); } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="attributes-editor">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[12px] font-semibold text-slate-700">Attributes</div>
          <p className="text-[11px] text-slate-500">Add a name and a value. A name with one value is shown as a detail (Author, ISBN).</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <select aria-label="Apply an attribute template" data-testid="attr-template-select" value="" disabled={!templates.length}
            onChange={(e) => { const t = templates.find((x) => x.id === e.target.value); if (t) apply(t.attributes, t.name); }}
            className="h-8 rounded-lg border border-slate-300 bg-white px-2 text-[12px] text-neutral-800 disabled:opacity-50 max-w-[200px]">
            <option value="">Use template…</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.attributes.length})</option>)}
          </select>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="mt-2.5 rounded-lg border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-[1fr_1fr_32px] gap-2 px-2 py-1.5 bg-slate-50 text-[11px] font-semibold text-slate-500">
            <span>Name</span><span>Value</span><span />
          </div>
          <datalist id="attr-names">{names.map((n) => <option key={n} value={n} />)}</datalist>
          {rows.map((r, i) => {
            const e = (showErrors || r.name || r.value) ? errors[i] : undefined;
            return (
              <div key={i} className="px-2 py-1.5 border-t border-slate-100" data-testid={`attr-row-${i}`}>
                <div className="grid grid-cols-[1fr_1fr_32px] gap-2 items-center">
                  <input value={r.name} onChange={(ev) => setRow(i, "name", ev.target.value)} list="attr-names" maxLength={40} placeholder="e.g. Author" aria-label="Attribute name" data-testid={`attr-name-${i}`} aria-invalid={e ? "true" : "false"} className={`${cell} ${e ? bad : ok}`} />
                  <input value={r.value} onChange={(ev) => setRow(i, "value", ev.target.value)} maxLength={80} placeholder="e.g. Jane Doe" aria-label="Attribute value" data-testid={`attr-value-${i}`} className={`${cell} ${e ? bad : ok}`} />
                  <button type="button" onClick={() => removeRow(i)} aria-label="Remove attribute" data-testid={`attr-remove-${i}`} className="w-8 h-8 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center"><Trash2 size={14} /></button>
                </div>
                {e && <p role="alert" className="mt-1 text-[11px] text-red-600">{e}</p>}
              </div>
            );
          })}
        </div>
      )}
      {errors[-1] && <p role="alert" className="mt-1 text-[11px] text-red-600">{errors[-1]}</p>}

      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <button type="button" onClick={addRow} data-testid="attr-add" className={btnOutline}><Plus size={14} /> Add attribute</button>
        {filled && !naming && <button type="button" onClick={() => setNaming(true)} data-testid="attr-save-template" className="ml-auto inline-flex items-center gap-1 text-[12px] font-semibold text-slate-600 hover:text-blue-600"><BookmarkPlus size={14} /> Save as template</button>}
        {naming && (
          <div className="ml-auto flex items-center gap-1.5">
            <input value={tplName} onChange={(e) => setTplName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveTemplate(); } }} autoFocus maxLength={60} placeholder="Template name" aria-label="Template name" data-testid="attr-template-name" className={`${cell} ${ok} w-40`} />
            <button type="button" onClick={saveTemplate} disabled={saving} data-testid="attr-template-confirm" className={btnOutline}><Save size={13} /> {saving ? "Saving…" : "Save"}</button>
            <button type="button" onClick={() => { setNaming(false); setTplName(""); }} aria-label="Cancel" className="w-8 h-8 rounded-md text-slate-400 hover:bg-slate-100 flex items-center justify-center"><X size={14} /></button>
          </div>
        )}
      </div>

      {templates.length > 0 && (
        <details className="mt-2 text-[12px] text-slate-500">
          <summary className="cursor-pointer select-none hover:text-slate-700">Manage templates ({templates.length})</summary>
          <ul className="mt-1.5 space-y-1">
            {templates.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2 py-1">
                <span className="truncate"><Copy size={11} className="inline mr-1 text-slate-400" />{t.name} · {t.attributes.length} attribute{t.attributes.length > 1 ? "s" : ""}</span>
                <button type="button" onClick={() => delTemplate(t)} aria-label={`Delete template ${t.name}`} className="text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
